import React from 'react'
import compact from 'lodash/compact'
import keyBy from 'lodash/keyBy'
import { Box, CircularProgress, Dialog, Typography } from '@mui/material'
import { useStore } from '@tanstack/react-form'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  marketingCheckoutCostsCreate,
  type CartRequest,
  type CheckoutShiftRequest,
} from '@sidegigllc/api/client'
import { ApiError } from '@sidegigllc/api/resources'
import { useCart } from '../../providers/Cart'
import { useSelectedMarket } from '../../providers/Market'
import { useMarketingWages } from '../../hooks/useMarketingWages'
import { SideRail } from './SideRail'
import { Stepper } from './Stepper'
import { STEP_LABELS, type StepIndex } from './schema'
import { useCheckoutForm } from './useCheckoutForm'
import { BusinessContactStep } from './steps/BusinessContactStep'
import { ShiftVerificationStep } from './steps/ShiftVerificationStep'
import type { ConfirmPaymentFn } from './steps/PaymentStep'
import { useCheckoutSession, type Session } from './useCheckoutSession'
import { CheckoutSuccess, type ConfirmSuccess } from './CheckoutSuccess'
import { CheckoutConflict } from './CheckoutConflict'
import type { RequestExit } from './ExitConfirm'
import { MonoButton } from './components'
import { monoHelperSx } from './styles'
import { applyApiFieldErrors, setFieldError, toE164 } from './utils'
import {
  identifyQwickGoLead,
  identifyQwickGoUser,
  trackQwickGoCheckoutCompleted,
  trackQwickGoCheckoutContactSubmitted,
  trackQwickGoCheckoutOpened,
  trackQwickGoCheckoutPaymentAuthorized,
  trackQwickGoCheckoutPhoneConflict,
  trackQwickGoCheckoutShiftSubmitted,
} from '../../utils/analytics'
import { NO_BUSINESS_ERROR } from './AddressAutocomplete'
import {
  clearPersisted,
  detectStripeReturn,
  loadPersisted,
  savePersisted,
  stripStripeReturnParams,
} from './persistence'

const PaymentStep = React.lazy(() => import('./steps/PaymentStep'))

const DURATION_OPTIONS = [4, 5, 6, 7, 8, 9, 10] as const

const costsQueryOptions = (cart: CartRequest) =>
  queryOptions({
    queryKey: ['marketingCheckoutCosts', cart],
    queryFn: () => marketingCheckoutCostsCreate(cart),
    staleTime: 30_000,
  })

const CONTACT_FIELD_MAP = {
  first_name: 'firstName',
  last_name: 'lastName',
  email: 'email',
  phone_number: 'phone',
  company: 'company',
  address: 'address',
} as const

type CheckoutBodyProps = {
  requestExit: RequestExit
}

export const CheckoutBody: React.FC<CheckoutBodyProps> = ({ requestExit }) => {
  const cart = useCart()
  const { market } = useSelectedMarket()
  const { data: skills } = useMarketingWages()

  const skillByName = React.useMemo(() => {
    const map = new Map<string, { id: number; wage: number }>()
    skills?.forEach((s) => {
      if (s.recommended_wage != null) {
        map.set(s.name, { id: s.id, wage: s.recommended_wage })
      }
    })
    return map
  }, [skills])

  const persistedAtMount = React.useMemo(() => loadPersisted(), [])
  const stripeReturn = React.useMemo(() => detectStripeReturn(), [])

  const defaultPositions = React.useMemo(
    () =>
      cart.items.map((i) => ({
        skillName: i.skillName,
        startTime: '08:00',
        duration: i.hours,
      })),
    // Snapshot at dialog mount; cart changes during checkout don't reset form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Plain reopen restores only contact fields; positions/date re-derive from cart.
  const persistedForm = persistedAtMount?.formValues ?? null
  const initialFormValues = !persistedForm
    ? null
    : stripeReturn
      ? persistedForm
      : { ...persistedForm, date: '', positions: defaultPositions }

  // Step/payment restore only on a Stripe 3DS redirect; session restores always (see below).
  const restoredState = stripeReturn ? persistedAtMount : null
  const [step, setStep] = React.useState<StepIndex>(restoredState?.step ?? 0)
  const [cardComplete, setCardComplete] = React.useState(false)
  const [stepSubmitting, setStepSubmitting] = React.useState(false)
  const [conflictPhone, setConflictPhone] = React.useState<string | null>(null)
  const [clientSecret, setClientSecret] = React.useState<string | null>(
    restoredState?.clientSecret ?? null,
  )
  const [paymentError, setPaymentError] = React.useState<string | null>(null)
  const [confirmed, setConfirmed] = React.useState<ConfirmSuccess | null>(null)
  const [paymentAuthorized, setPaymentAuthorized] = React.useState(
    restoredState?.paymentAuthorized ?? false,
  )
  const [paymentLast4, setPaymentLast4] = React.useState<string | null>(
    restoredState?.paymentLast4 ?? null,
  )
  const [shiftUnlocked, setShiftUnlocked] = React.useState(
    restoredState?.shiftUnlocked ?? false,
  )
  const confirmPaymentRef = React.useRef<ConfirmPaymentFn | null>(null)
  const checkoutSession = useCheckoutSession(persistedAtMount?.session ?? null)
  const queryClient = useQueryClient()

  React.useEffect(() => {
    void import('./steps/PaymentStep')
  }, [])

  // Fire once on a genuine open, not a Stripe 3DS resume.
  React.useEffect(() => {
    if (stripeReturn) return
    trackQwickGoCheckoutOpened({
      item_count: cart.items.reduce((sum, i) => sum + i.quantity, 0),
      position_count: cart.items.length,
      line_items: cart.items.map((i) => ({
        role: i.skillName,
        qty: i.quantity,
        hrs: i.hours,
      })),
      market_id: market?.marketId,
      market: market?.marketName,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Warn before tab close once a card hold is in place.
  React.useEffect(() => {
    if (!paymentAuthorized) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // returnValue still required by older Chromium + Safari.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [paymentAuthorized])

  const form = useCheckoutForm({
    defaultPositions,
    initialValues: initialFormValues,
    onSubmit: async () => {},
  })

  const formValues = useStore(form.store, (s) => s.values)
  const positions = formValues.positions

  // Keyed by skillName — cart dedupes by skill (one line per skill).
  const posBySkill = React.useMemo(
    () => keyBy(positions, 'skillName'),
    [positions],
  )

  const summaryItems = cart.items.map((i) => {
    const rate = skillByName.get(i.skillName)?.wage ?? 0
    const hours = posBySkill[i.skillName]?.duration ?? i.hours
    return { item: { ...i, hours }, rate, subtotal: rate * hours * i.quantity }
  })

  const cartRequest = React.useMemo(
    () => ({
      cart: compact(
        cart.items.map((i) => {
          const s = skillByName.get(i.skillName)
          if (!s) return null
          const hrs = posBySkill[i.skillName]?.duration ?? i.hours
          return { skill_id: s.id, qty: i.quantity, hrs, rate: s.wage }
        }),
      ),
    }),
    [cart.items, skillByName, posBySkill],
  )

  // Omitting date/start_time resets the line to unscheduled server-side.
  const buildScheduledCart = (date: string): CartRequest => ({
    cart: compact(
      cart.items.map((i) => {
        const s = skillByName.get(i.skillName)
        if (!s) return null
        return {
          skill_id: s.id,
          qty: i.quantity,
          hrs: posBySkill[i.skillName]?.duration ?? i.hours,
          rate: s.wage,
          date,
          start_time: `${posBySkill[i.skillName]?.startTime ?? ''}:00`,
        }
      }),
    ),
  })

  // /shift is positional — derive from the cart lines.
  const buildShifts = (date: string): CheckoutShiftRequest => ({
    shifts: buildScheduledCart(date).cart.map((line) => ({
      date,
      // buildScheduledCart always sets start_time; the `??` only satisfies the
      // optional-vs-required mismatch between the cart and shift request types.
      start_time: line.start_time ?? '',
    })),
  })

  const validateCurrentStep = async (): Promise<boolean> => {
    if (step === 2) return paymentAuthorized || cardComplete
    // Only the current step's fields are mounted, so this validates just them.
    const errs = await form.validateAllFields('change')
    return errs.length === 0
  }

  const goPrev = () => {
    if (step === 0) {
      requestExit(() => {
        cart.closeCheckout()
        cart.open()
      })
    } else {
      setStep((s) => (s - 1) as StepIndex)
    }
  }

  const runConfirm = async () => {
    const v = form.state.values
    const res = await checkoutSession.submitConfirm()
    if (res.card_last4) setPaymentLast4(res.card_last4)
    if (res.user_id) identifyQwickGoUser(String(res.user_id), market?.marketId)
    trackQwickGoCheckoutCompleted({
      session_id: checkoutSession.session?.id ?? '',
      external_id: String(res.user_id),
      organization_id: res.organization_id,
      location_id: res.location_id,
      gig_external_ids: res.gig_external_ids,
      gig_count: res.gig_external_ids.length,
      wages: costsQuery.data?.wages,
      date: v.date,
      market_id: market?.marketId,
      market: market?.marketName,
    })
    setConfirmed({
      locationId: res.location_id,
      phoneE164: toE164(v.phone),
      date: v.date,
    })
    clearPersisted()
  }

  // /confirm failed but the hold may persist; recover last4 to show the saved card on retry.
  const recoverLast4 = async () => {
    try {
      const sess = await checkoutSession.retrieveSession()
      if (sess.card_last4) setPaymentLast4(sess.card_last4)
    } catch (e) {
      console.error('Checkout retrieve (last4) failed', e)
    }
  }

  const handleContactStep = async () => {
    if (cartRequest.cart.length === 0 || !market) return
    const v = form.state.values
    if (!v.resolvedAddress) {
      setFieldError(form, 'address', NO_BUSINESS_ERROR)
      return
    }
    setStepSubmitting(true)
    let s: Session | null = null
    try {
      s = await checkoutSession.ensureSession(cartRequest)
      const res = await checkoutSession.submitContact(s, {
        first_name: v.firstName,
        last_name: v.lastName,
        email: v.email,
        phone_number: toE164(v.phone),
        company: v.company,
        address: v.resolvedAddress,
      })
      identifyQwickGoLead({
        first_name: v.firstName,
        last_name: v.lastName,
        email: v.email,
        phone_number: toE164(v.phone),
        location_name: v.company,
        market_id: market?.marketId,
        address: v.resolvedAddress,
      })
      trackQwickGoCheckoutContactSubmitted({
        session_id: s.id,
        first_name: v.firstName,
        last_name: v.lastName,
        email: v.email,
        phone_number: toE164(v.phone),
        location_name: v.company,
        is_existing_location_match: res.is_existing_location_match,
        market_id: market?.marketId,
        market: market?.marketName,
        address: v.resolvedAddress,
      })
      if (res.is_existing_location_match) {
        setFieldError(
          form,
          'address',
          'This location is already on Qwick. Log in instead to post.',
        )
        return
      }
      setStep(1)
    } catch (e) {
      const apiErr = ApiError.fromThrown<Record<string, string>>(e)
      // 409 = phone already belongs to an account → show login screen.
      if (apiErr.httpStatus === 409) {
        trackQwickGoCheckoutPhoneConflict({
          session_id: s?.id ?? '',
          phone_number: toE164(v.phone),
          account_type: apiErr.getFieldErrorMessage('account_type'),
          market_id: market?.marketId,
          market: market?.marketName,
        })
        setConflictPhone(toE164(v.phone))
        return
      }
      const mapped = applyApiFieldErrors(form, apiErr, CONTACT_FIELD_MAP)
      if (!mapped) console.error('Checkout /contact failed', e)
    } finally {
      setStepSubmitting(false)
    }
  }

  const handleShiftStep = async () => {
    // Hold already placed and shift locked → session row has /shift + /payment.
    if (paymentAuthorized && !shiftUnlocked) {
      setStep(2)
      return
    }
    const v = form.state.values
    const first = v.positions[0]
    if (!first) return
    setStepSubmitting(true)
    try {
      await checkoutSession.submitCart(buildScheduledCart(v.date))
      await checkoutSession.submitShift(buildShifts(v.date))
      const [pay] = await Promise.all([
        checkoutSession.submitPayment(),
        queryClient.prefetchQuery(costsQueryOptions(cartRequest)),
      ])
      trackQwickGoCheckoutShiftSubmitted({
        session_id: checkoutSession.session?.id ?? '',
        date: v.date,
        item_count: v.positions.length,
        shifts: v.positions.map((p) => ({
          role: p.skillName,
          start_time: p.startTime,
          duration_hours: p.duration,
        })),
        market_id: market?.marketId,
        market: market?.marketName,
      })
      setClientSecret(pay.client_secret)
      setStep(2)
    } catch (e) {
      const apiErr = ApiError.fromThrown<{
        date: string
        start_time: string
      }>(e)
      const dateErr = apiErr.getFieldErrorMessage('date')
      const startErr = apiErr.getFieldErrorMessage('start_time')
      if (dateErr) setFieldError(form, 'date', dateErr)
      if (startErr) setFieldError(form, 'positions', startErr)
      if (!dateErr && !startErr) console.error('Checkout /shift failed', e)
    } finally {
      setStepSubmitting(false)
    }
  }

  const handlePaymentStep = async () => {
    setPaymentError(null)
    setStepSubmitting(true)
    try {
      if (!paymentAuthorized) {
        const confirm = confirmPaymentRef.current
        if (!confirm) return
        const { error } = await confirm()
        if (error) {
          setPaymentError(error.message ?? 'Payment failed')
          return
        }
        setPaymentAuthorized(true)
        // last4 only comes from the API (the BE stores it post-authorization).
        await recoverLast4()
        trackQwickGoCheckoutPaymentAuthorized({
          session_id: checkoutSession.session?.id ?? '',
          wages: costsQuery.data?.wages,
          platform_fee: costsQuery.data?.platform_fee,
          total: costsQuery.data?.total,
          market_id: market?.marketId,
          market: market?.marketName,
        })
      }
      await runConfirm()
    } catch (e) {
      console.error('Checkout /confirm failed', e)
      setPaymentError('Could not finalize your booking. Please try again.')
      await recoverLast4()
    } finally {
      setStepSubmitting(false)
    }
  }

  const goNext = async () => {
    if (!(await validateCurrentStep())) return
    if (step === 0) return handleContactStep()
    if (step === 1) return handleShiftStep()
    if (step === 2) return handlePaymentStep()
    void form.handleSubmit()
  }

  const handleSuccessClose = () => {
    clearPersisted()
    cart.clear()
    setConfirmed(null)
  }

  const handleUseDifferentCard = async () => {
    setPaymentError(null)
    setPaymentAuthorized(false)
    setPaymentLast4(null)
    setClientSecret(null)
    try {
      await checkoutSession.submitCart(
        buildScheduledCart(form.state.values.date),
      )
      const res = await checkoutSession.submitPayment()
      setClientSecret(res.client_secret)
    } catch (e) {
      console.error('Checkout /payment (reissue) failed', e)
      setPaymentError('Could not reset payment. Please refresh and try again.')
    }
  }

  const handleUnlockShift = async () => {
    // Re-entering card requires re-running /payment; clear the flag + cached secret.
    setPaymentAuthorized(false)
    setPaymentLast4(null)
    setClientSecret(null)
    setShiftUnlocked(true)
  }

  // Persist for Stripe 3DS resume + close/reopen. Save once a session exists, or
  // input is dirty before it (contact data on step 0).
  React.useEffect(() => {
    if (confirmed) return
    if (!checkoutSession.session && !form.state.isDirty) return
    savePersisted({
      session: checkoutSession.session,
      cartItems: cart.items,
      formValues,
      step,
      clientSecret,
      paymentAuthorized,
      paymentLast4,
      shiftUnlocked,
    })
  }, [
    confirmed,
    checkoutSession.session,
    form.state.isDirty,
    cart.items,
    formValues,
    step,
    clientSecret,
    paymentAuthorized,
    paymentLast4,
    shiftUnlocked,
  ])

  // Back from 3DS with bank approval → finalize via /confirm (cart already restored).
  const autoConfirmRanRef = React.useRef(false)
  React.useEffect(() => {
    if (autoConfirmRanRef.current) return
    if (!stripeReturn) return
    if (!persistedAtMount) return
    autoConfirmRanRef.current = true
    stripStripeReturnParams()
    if (stripeReturn.redirectStatus !== 'succeeded') {
      setPaymentError(
        stripeReturn.redirectStatus === 'failed'
          ? 'Payment failed. Please try a different card.'
          : 'Payment was not completed.',
      )
      return
    }
    setStepSubmitting(true)
    runConfirm()
      .catch(async (e) => {
        console.error('Checkout /confirm (post-redirect) failed', e)
        setPaymentError('Could not finalize your booking. Please try again.')
        await recoverLast4()
      })
      .finally(() => setStepSubmitting(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const costsQuery = useQuery({
    ...costsQueryOptions(cartRequest),
    enabled:
      step === 2 &&
      cartRequest.cart.length > 0 &&
      cartRequest.cart.length === cart.items.length,
  })

  // Stacked over the still-mounted form so "use different details" returns to it.
  const terminalPaperSx = {
    m: 2,
    maxWidth: 440,
    width: '100%',
    borderRadius: '6px',
    backgroundColor: 'common.white',
  }

  return (
    <>
      <Dialog
        open={!!confirmed}
        onClose={(_e, reason) => {
          // Booking is committed; only the in-card button may dismiss this.
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
        }}
        maxWidth={false}
        slotProps={{ paper: { sx: terminalPaperSx } }}
      >
        {confirmed && (
          <CheckoutSuccess confirmed={confirmed} onClose={handleSuccessClose} />
        )}
      </Dialog>
      <Dialog
        open={!!conflictPhone}
        onClose={() => setConflictPhone(null)}
        maxWidth={false}
        slotProps={{ paper: { sx: terminalPaperSx } }}
      >
        {conflictPhone && (
          <CheckoutConflict
            phoneE164={conflictPhone}
            onBack={() => setConflictPhone(null)}
          />
        )}
      </Dialog>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void goNext()
        }}
        sx={{
          flex: 1,
          minHeight: { xs: 0, sm: 520 },
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            p: { xs: '24px 22px', sm: '32px 36px' },
            overflowY: 'auto',
          }}
        >
          <MonoButton
            variant="text"
            type="button"
            onClick={goPrev}
            sx={{
              p: 0,
              py: 0,
              minWidth: 0,
              border: 'none',
              borderBottom: 'none !important',
              fontSize: 11,
              color: 'neutral.gray',
              mb: 0.5,
              '&:hover, &:focus-visible': {
                background: 'none',
                color: 'common.black',
                borderBottom: 'none',
              },
            }}
          >
            {step === 0 ? '← Back to roster' : '← Previous step'}
          </MonoButton>

          <Stepper current={step} />

          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontSize: 22,
              fontWeight: 900,
              textTransform: 'uppercase',
              m: '0 0 22px',
            }}
          >
            {STEP_LABELS[step]}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {step === 0 && <BusinessContactStep form={form} />}
            {step === 1 && (
              <ShiftVerificationStep
                form={form}
                durationOptions={[...DURATION_OPTIONS]}
                skillByName={skillByName}
                locked={paymentAuthorized && !shiftUnlocked}
                onUnlock={handleUnlockShift}
              />
            )}
            {step === 2 && (
              <React.Suspense
                fallback={
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                }
              >
                <PaymentStep
                  items={summaryItems}
                  costs={costsQuery.data ?? null}
                  clientSecret={clientSecret}
                  authorized={paymentAuthorized}
                  last4={paymentLast4}
                  submitting={stepSubmitting}
                  onCardChange={setCardComplete}
                  onConfirmReady={(fn) => {
                    confirmPaymentRef.current = fn
                  }}
                  onUseDifferentCard={handleUseDifferentCard}
                />
              </React.Suspense>
            )}
          </Box>

          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => {
              const busy = submitting || stepSubmitting
              const label = busy
                ? 'Processing…'
                : step === 2
                  ? 'Submit Roster'
                  : 'Continue →'
              return (
                <MonoButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={busy}
                  fullWidth
                  startIcon={
                    busy ? (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        color="inherit"
                      />
                    ) : undefined
                  }
                  sx={{
                    mt: 2,
                    '&.Mui-disabled': {
                      opacity: 0.7,
                      cursor: 'not-allowed',
                      backgroundColor: 'primary.main',
                      color: 'common.white',
                    },
                  }}
                >
                  {label}
                </MonoButton>
              )
            }}
          </form.Subscribe>

          {step === 2 && paymentError && (
            <Typography
              sx={{
                fontFamily: (theme) => theme.app.monoFontFamily,
                fontSize: 11,
                color: 'error.main',
                textAlign: 'center',
                mt: 1.25,
              }}
            >
              {paymentError}
            </Typography>
          )}

          {step === 2 && (
            <Typography sx={[monoHelperSx, { textAlign: 'center', mt: 1.25 }]}>
              We will place a hold for the full amount on your card now and
              charge you when the shift completes.
            </Typography>
          )}
        </Box>

        <SideRail step={step} />
      </Box>
    </>
  )
}
